package com.vamvisions.main.api.portfolio

import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

/** A date known to the year, or to the year and [month] (1-12). Serialized as `"YYYY"` or `"YYYY-MM"`. */
@Serializable(with = PartialDateSerializer::class)
data class PartialDate(val year: Int, val month: Int? = null) {
    init {
        require(year in 0..9999) { "year must be from 0 to 9999, was $year" }
        require(month == null || month in 1..12) { "month must be from 1 to 12, was $month" }
    }

    override fun toString(): String =
        year.toString().padStart(4, '0') + (month?.let { "-" + it.toString().padStart(2, '0') } ?: "")

    companion object {
        private val pattern = Regex("""(\d{4})(?:-(\d{2}))?""")

        /** Parses `"YYYY"` or `"YYYY-MM"`, or returns null when [value] is not a valid partial date. */
        fun parseOrNull(value: String): PartialDate? {
            val match = pattern.matchEntire(value) ?: return null
            val year = match.groupValues[1].toInt()
            val month = match.groupValues[2].takeIf { it.isNotEmpty() }?.toInt()
            if (month != null && month !in 1..12) return null
            return PartialDate(year, month)
        }
    }
}

object PartialDateSerializer : KSerializer<PartialDate> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("com.vamvisions.main.api.portfolio.PartialDate", PrimitiveKind.STRING)

    override fun serialize(encoder: Encoder, value: PartialDate) = encoder.encodeString(value.toString())

    override fun deserialize(decoder: Decoder): PartialDate {
        val value = decoder.decodeString()
        return PartialDate.parseOrNull(value)
            ?: throw SerializationException("'$value' is not a partial date; expected YYYY or YYYY-MM")
    }
}
